import type { PublicDashboardResultsResponse } from '../shared/api';
import type { SurveyCatalogEntry, SurveyKey } from '../shared/survey';
import { api, escapeHtml } from './http';
import { pageLayout } from './layout';
import { publicNeighborhoodDisplayName } from './neighborhood-display';
import {
  createResultViewState,
  installResultOptionMenus,
  renderActiveModifiers,
  renderResultQuestionCard,
  type ResultViewState,
} from './results-page';

export async function renderPublicResultsDashboardPage(root: HTMLElement): Promise<void> {
  const showComments = new URLSearchParams(window.location.search).get('show-comments') === 'true';
  root.innerHTML = pageLayout('Survey Results', '<div id="publicResultsDashboard"><div class="loading-state"><span class="spinner"></span>Loading results…</div></div>', {
    publicNav: true,
    wide: true,
    showAbout: false,
  });
  const content = root.querySelector<HTMLElement>('#publicResultsDashboard')!;
  try {
    const { surveys } = await api<{ surveys: SurveyCatalogEntry[] }>('/api/surveys');
    await loadPublicResultsDashboard(content, surveys, 'original', null, createResultViewState(), showComments);
  } catch (error) {
    content.innerHTML = renderError(error);
  }
}

async function loadPublicResultsDashboard(
  content: HTMLElement,
  surveys: SurveyCatalogEntry[],
  surveyKey: SurveyKey,
  neighborhood: string | null,
  viewState: ResultViewState,
  showComments: boolean,
): Promise<void> {
  try {
    const query = createPublicDashboardQuery(surveyKey, neighborhood, viewState, showComments);
    const results = await api<PublicDashboardResultsResponse>(`/api/public-results-dashboard${query}`);
    content.innerHTML = `
      <section class="dashboard-toolbar public-dashboard-toolbar card">
        <label>Survey<select id="surveyFilter">${surveys.map((survey) => `<option value="${survey.key}"${survey.key === results.surveyKey ? ' selected' : ''}>${escapeHtml(survey.label)}</option>`).join('')}</select></label>
        <label>Results view<select id="neighborhoodFilter"><option value="">All neighborhoods</option>${results.neighborhoods.map((item) => `<option value="${escapeHtml(item)}"${item === results.neighborhood ? ' selected' : ''}>${escapeHtml(publicNeighborhoodDisplayName(item))}</option>`).join('')}</select></label>
        <div class="summary-stat"><strong>${results.responseCount}</strong><span>Responses</span></div>
        <div class="summary-stat"><strong>${results.householdCount}</strong><span>Households</span></div>
      </section>
      ${renderActiveModifiers(viewState)}
      <section class="card duplicate-panel">
        <div class="result-header">
          <div><p class="eyebrow">Access-code usage</p><h2>Most repeated access codes</h2></div>
          <div class="duplicate-summary" aria-label="Access-code duplication summary">
            <span><strong>${results.repeatedAccessCodeCount}</strong> codes used more than once</span>
            <span><strong>${results.additionalResponseCount}</strong> additional responses</span>
          </div>
        </div>
        ${results.duplicateAccessCodes.length === 0
          ? '<p class="empty-state">No access code has more than one response in this view.</p>'
          : `<div class="duplicate-table-wrap"><table class="duplicate-table">
              <thead><tr><th scope="col">Rank</th><th scope="col">Access code</th><th scope="col">Neighborhood</th><th scope="col">Responses</th></tr></thead>
              <tbody>${results.duplicateAccessCodes.map((entry, index) => `<tr>
                <td>${index + 1}</td><td><code>${escapeHtml(entry.accessCode)}</code></td><td>${escapeHtml(publicNeighborhoodDisplayName(entry.neighborhood))}</td><td><strong>${entry.responseCount}</strong></td>
              </tr>`).join('')}</tbody>
            </table></div>
            ${results.repeatedAccessCodeCount > results.duplicateAccessCodes.length ? `<p class="duplicate-limit">Showing the top ${results.duplicateAccessCodes.length} of ${results.repeatedAccessCodeCount} repeated codes.</p>` : ''}`}
      </section>
      <section class="card fraud-panel">
        <div class="result-header">
          <div><p class="eyebrow">Submission integrity</p><h2>Fraud detection signals</h2></div>
          <span>Sorted by submission count</span>
        </div>
        <p class="section-intro">Compare repeated network addresses and browsers with the number of access codes they used. These signals can be shared by legitimate household members and should be reviewed together.</p>
        <div class="fraud-grid">
          ${renderSignalTable('IP address', results.submissionsByIpAddress)}
          ${renderSignalTable('User agent', results.submissionsByUserAgent)}
        </div>
      </section>
      <div class="results-grid">${results.questions.map((question, index) => renderResultQuestionCard(question, index, viewState)).join('')}</div>
      ${showComments ? `<section class="card feedback-panel">
        <div class="result-header"><div><p class="eyebrow">Optional responses</p><h2>Additional feedback</h2></div><span>${results.feedback.length} entries</span></div>
        ${results.feedback.length === 0 ? '<p class="empty-state">No additional feedback in this view.</p>' : `<div class="feedback-list">${results.feedback.map((entry) => `<article><p>${escapeHtml(entry.text)}</p><footer>${escapeHtml(publicNeighborhoodDisplayName(entry.neighborhood))} · ${new Date(entry.submittedAt).toLocaleString()}</footer></article>`).join('')}</div>`}
      </section>` : ''}`;

    content.querySelector<HTMLSelectElement>('#surveyFilter')?.addEventListener('change', (event) => {
      void loadPublicResultsDashboard(content, surveys, (event.currentTarget as HTMLSelectElement).value as SurveyKey, null, createResultViewState(), showComments);
    });
    content.querySelector<HTMLSelectElement>('#neighborhoodFilter')?.addEventListener('change', (event) => {
      const value = (event.currentTarget as HTMLSelectElement).value;
      void loadPublicResultsDashboard(content, surveys, results.surveyKey, value || null, viewState, showComments);
    });
    installResultOptionMenus(content, viewState, () => loadPublicResultsDashboard(content, surveys, results.surveyKey, results.neighborhood, viewState, showComments));
  } catch (error) {
    content.innerHTML = renderError(error);
  }
}

function renderSignalTable(label: string, entries: PublicDashboardResultsResponse['submissionsByIpAddress']): string {
  return `<div class="signal-table-wrap"><h3>By ${label.toLowerCase()}</h3>
    ${entries.length === 0 ? '<p class="empty-state">No submissions in this view.</p>' : `<table class="signal-table">
      <thead><tr><th scope="col">${label}</th><th scope="col">Submissions</th><th scope="col">Codes</th></tr></thead>
      <tbody>${entries.map((entry) => `<tr><td title="${escapeHtml(entry.value)}"><code>${escapeHtml(entry.value)}</code></td><td><strong>${entry.responseCount}</strong></td><td>${entry.accessCodeCount}</td></tr>`).join('')}</tbody>
    </table>`}
  </div>`;
}

function createPublicDashboardQuery(surveyKey: SurveyKey, neighborhood: string | null, viewState: ResultViewState, showComments: boolean): string {
  const parameters = new URLSearchParams();
  if (showComments) parameters.set('show-comments', 'true');
  if (surveyKey !== 'original') parameters.set('surveyKey', surveyKey);
  if (neighborhood) parameters.set('neighborhood', neighborhood);
  viewState.facets.forEach(({ questionId, option }) => parameters.append('facet', `${questionId}:${option}`));
  viewState.exclusions.forEach(({ questionId, option }) => parameters.append('exclude', `${questionId}:${option}`));
  return parameters.size ? `?${parameters}` : '';
}

function renderError(error: unknown): string {
  return `<div class="error-state">${escapeHtml(error instanceof Error ? error.message : 'Results could not be loaded.')}</div>`;
}
